import { clientContacts, jobContactDuties, jobContactTags, positionIsRoleWord, siteContacts } from '@fieldops/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { ContactFields, type ClientPerson } from '../client/ContactList.js'
import { Icon } from '../Icon.js'
import type { Job, JobContact } from '../job-card/types.js'
import { FieldLayer, GroupLabel, cardClass, telHref } from './kit.js'

type Person = ClientPerson & { optedOutOfNotifications?: boolean }
type Role = 'JOB' | 'SITE' | 'OTHER'
type Open = { kind: 'person'; contactId: string } | { kind: 'add' } | null

/**
 * The people on the job, on the phone: who to ring, and adding or fixing
 * somebody at the door.
 *
 * The same records and rules as the office's People (decisions 84 and 85):
 * one row per person, Job / Site / Billing on the link, and a new person
 * lands on the client as well as the job. A job nobody has been named on
 * shows the client's own contacts, as the office card does. Writes go
 * through the job (`jobs.createContact`, `jobs.saveContact`), which is
 * `JOB_EDIT` on a job of one's own, not `CLIENT_EDIT`.
 */
export function FieldPeople({
  job,
  canEdit,
  onText,
  onEmail,
}: {
  job: Job
  canEdit: boolean
  onText: (phone: string, name: string) => void
  onEmail: (email: string) => void
}) {
  const [open, setOpen] = useState<Open>(null)
  const duties = jobContactDuties(job.contacts)
  const fromClient = job.contacts.length === 0
  const rows: Array<{ contact: Person; link: JobContact | null }> = fromClient
    ? clientContacts(job.client.contacts).map((contact) => ({ contact, link: null }))
    : job.contacts.map((link) => ({ contact: link.contact, link }))

  const selected = open?.kind === 'person' ? rows.find((row) => row.contact.id === open.contactId) : undefined

  return (
    <section>
      <GroupLabel aside={fromClient && rows.length > 0 ? 'From the client' : undefined}>People</GroupLabel>
      <div className={cn(cardClass, 'divide-y divide-ink-100')}>
        {rows.map(({ contact, link }) => {
          const name = nameOf(contact)
          const number = contact.mobile ?? contact.phone
          const line = [
            titleOf(contact),
            ...(link ? jobContactTags(link, duties.sole) : contact.isPrimary ? ['Primary'] : []),
          ]
            .filter(Boolean)
            .join(' · ')
          return (
            <div key={contact.id} className="flex items-center gap-2 py-2 pr-2.5 pl-3.5">
              <button
                type="button"
                onClick={() => setOpen({ kind: 'person', contactId: contact.id })}
                className="flex min-h-11 min-w-0 flex-1 items-center gap-3 text-left"
              >
                <Initials name={name} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-semibold text-ink-900">{name}</span>
                  <span className="block truncate text-[13px] text-ink-500">{line || 'Client contact'}</span>
                </span>
              </button>
              {number ? (
                <a
                  href={telHref(number) ?? undefined}
                  aria-label={`Call ${name}`}
                  className="grid size-9 shrink-0 place-items-center rounded-md bg-brand-50 text-brand-700 active:bg-brand-100"
                >
                  <Icon name="phone" className="size-[18px]" />
                </a>
              ) : null}
              <Icon name="forward" className="size-4 shrink-0 text-ink-400" />
            </div>
          )
        })}
        {rows.length === 0 ? (
          <p className="px-3.5 py-3.5 text-[14px] text-ink-500">Nobody on this job or the client yet.</p>
        ) : null}
        {canEdit ? (
          <button
            type="button"
            onClick={() => setOpen({ kind: 'add' })}
            className="flex min-h-[50px] w-full items-center gap-3 px-3.5 text-left text-[15px] font-semibold text-brand-700 active:bg-ink-50"
          >
            <span className="grid size-9 shrink-0 place-items-center rounded-md bg-brand-50">
              <Icon name="plus" className="size-4" strokeWidth={2.2} />
            </span>
            Add a person
          </button>
        ) : null}
      </div>

      {selected ? (
        <PersonSheet
          job={job}
          contact={selected.contact}
          link={selected.link}
          sole={duties.sole}
          billing={selected.link !== null && duties.billing?.id === selected.link.id}
          canEdit={canEdit}
          onText={onText}
          onEmail={onEmail}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open?.kind === 'add' ? <AddPersonSheet job={job} onClose={() => setOpen(null)} /> : null}
    </section>
  )
}

function usePeopleWrites(onDone?: () => void) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const settled = {
    onSuccess: () => {
      void queryClient.invalidateQueries(trpc.jobs.pathFilter())
      void queryClient.invalidateQueries(trpc.clients.pathFilter())
      void queryClient.invalidateQueries(trpc.schedule.pathFilter())
    },
  }
  const closing = {
    onSuccess: () => {
      settled.onSuccess()
      onDone?.()
    },
  }
  return {
    add: useMutation(trpc.jobs.addContact.mutationOptions(closing)),
    create: useMutation(trpc.jobs.createContact.mutationOptions(closing)),
    save: useMutation(trpc.jobs.saveContact.mutationOptions(settled)),
    remove: useMutation(trpc.jobs.removeContact.mutationOptions(closing)),
    roles: useMutation(trpc.jobs.updateContact.mutationOptions(settled)),
    billing: useMutation(trpc.jobs.setBillingContact.mutationOptions(settled)),
  }
}

/** One person: reach them, say what they are on this job, fix their details. */
function PersonSheet({
  job,
  contact,
  link,
  sole,
  billing,
  canEdit,
  onText,
  onEmail,
  onClose,
}: {
  job: Job
  contact: Person
  link: JobContact | null
  sole: boolean
  billing: boolean
  canEdit: boolean
  onText: (phone: string, name: string) => void
  onEmail: (email: string) => void
  onClose: () => void
}) {
  const writes = usePeopleWrites(onClose)
  const [editing, setEditing] = useState(false)
  const name = nameOf(contact)
  const number = contact.mobile ?? contact.phone
  const roles = (link?.roles ?? []).filter((role): role is Role => role === 'JOB' || role === 'SITE' || role === 'OTHER')
  const optedOut = contact.optedOutOfNotifications === true
  const busy = writes.roles.isPending || writes.billing.isPending

  const toggle = (role: 'JOB' | 'SITE') => {
    if (!link || (role === 'JOB' && sole)) return
    const without = roles.filter((item) => item !== role)
    writes.roles.mutate({ id: link.id, roles: roles.includes(role) ? without : [...without, role] })
  }

  return (
    <FieldLayer title={name} subtitle={titleOf(contact) ?? (link ? 'On this job' : 'Client contact')} onClose={onClose}>
      <div className="mx-auto flex w-full max-w-[680px] flex-col gap-[18px] px-4 pt-4 pb-7">
        {number || contact.email ? (
          <div className="grid grid-cols-3 gap-2">
            <Reach label="Call" icon="phone" href={telHref(number)} />
            <Reach
              label="Text"
              icon="inbox"
              onClick={contact.mobile ? () => onText(contact.mobile!, name) : undefined}
            />
            <Reach label="Email" icon="mail" onClick={contact.email ? () => onEmail(contact.email!) : undefined} />
          </div>
        ) : null}

        {link && canEdit ? (
          <section>
            <GroupLabel>On this job</GroupLabel>
            <div className={cn(cardClass, 'flex flex-col gap-3 p-3.5')}>
              <div role="group" aria-label={`What ${name} is on this job`} className="grid grid-cols-3 gap-1 rounded-md bg-ink-100 p-1">
                <Segment pressed={sole || roles.includes('JOB')} disabled={sole || busy} onClick={() => toggle('JOB')}>
                  Job
                </Segment>
                <Segment pressed={roles.includes('SITE')} disabled={busy} onClick={() => toggle('SITE')}>
                  Site
                </Segment>
                <Segment
                  pressed={sole || billing}
                  disabled={sole || billing || busy}
                  onClick={() => writes.billing.mutate({ jobId: job.id, jobContactId: link.id })}
                >
                  Billing
                </Segment>
              </div>
              {roles.includes('OTHER') && link.label ? (
                <p className="text-[13px] text-ink-500">Also: {link.label}</p>
              ) : null}
              <label className="flex min-h-11 items-center justify-between gap-3">
                <span className={cn('text-[15px]', optedOut ? 'text-ink-400' : 'text-ink-800')}>
                  {optedOut ? 'Opted out of comms' : 'Gets comms about this job'}
                </span>
                <input
                  type="checkbox"
                  role="switch"
                  aria-label={`Gets comms for ${name}`}
                  checked={link.receivesComms && !optedOut}
                  disabled={optedOut || busy}
                  onChange={(event) => writes.roles.mutate({ id: link.id, receivesComms: event.target.checked })}
                  className="h-[31px] w-[51px] shrink-0 cursor-pointer appearance-none rounded-md bg-ink-300 transition-colors before:block before:size-[27px] before:translate-x-0.5 before:rounded-sm before:bg-white before:shadow before:transition-transform checked:bg-ink-900 checked:before:translate-x-[22px] disabled:opacity-50"
                />
              </label>
            </div>
          </section>
        ) : null}

        {!link && canEdit ? (
          <button
            type="button"
            disabled={writes.add.isPending}
            onClick={() => writes.add.mutate({ jobId: job.id, contactId: contact.id })}
            className="h-12 rounded-md bg-brand-500 text-base font-semibold text-ink-900 disabled:opacity-50"
          >
            Add {contact.firstName} to this job
          </button>
        ) : null}

        <section>
          <GroupLabel
            aside={
              canEdit && !editing ? (
                <button
                  type="button"
                  aria-label={`Edit ${name}`}
                  onClick={() => setEditing(true)}
                  className="text-[15px] font-semibold tracking-normal text-brand-700 normal-case"
                >
                  Edit
                </button>
              ) : undefined
            }
          >
            Details
          </GroupLabel>
          {editing ? (
            <FormCard>
              <ContactFields
                idPrefix={`field-${contact.id}`}
                person={contact}
                submitLabel="Save"
                isPending={writes.save.isPending}
                error={writes.save.error ? errorMessage(writes.save.error) : null}
                atSite={Boolean(contact.siteId)}
                onCancel={() => setEditing(false)}
                onSubmit={(values) =>
                  writes.save.mutate(
                    { jobId: job.id, contactId: contact.id, ...values },
                    { onSuccess: () => setEditing(false) },
                  )
                }
              />
            </FormCard>
          ) : (
            <div className={cn(cardClass, 'divide-y divide-ink-100')}>
              {(
                [
                  ['Mobile', contact.mobile],
                  ['Phone', contact.phone],
                  ['Email', contact.email],
                  ['Role', titleOf(contact)],
                ] as const
              ).map(([label, value]) => (
                <div key={label} className="flex min-h-[48px] items-center justify-between gap-3 px-3.5">
                  <span className="text-ink-500">{label}</span>
                  <span className={cn('min-w-0 truncate text-right', value ? 'font-medium text-ink-900' : 'text-ink-400')}>
                    {value ?? 'Not on file'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {link && canEdit ? (
          <button
            type="button"
            disabled={writes.remove.isPending}
            onClick={() => writes.remove.mutate({ id: link.id })}
            className="h-12 rounded-md bg-white text-base font-semibold text-red-700 ring-1 ring-ink-200 ring-inset active:bg-red-50"
          >
            Remove from job
          </button>
        ) : null}
        {writes.roles.error || writes.billing.error || writes.add.error || writes.remove.error ? (
          <p role="alert" className="px-1 text-[13px] text-red-700">
            {errorMessage((writes.roles.error ?? writes.billing.error ?? writes.add.error ?? writes.remove.error)!)}
          </p>
        ) : null}
      </div>
    </FieldLayer>
  )
}

/**
 * Somebody for the job: one of the client's or this property's people who
 * is not on it yet, or somebody new typed in.
 */
function AddPersonSheet({ job, onClose }: { job: Job; onClose: () => void }) {
  const writes = usePeopleWrites(onClose)
  const [term, setTerm] = useState('')
  const [draft, setDraft] = useState<ClientPerson | null>(null)
  const [atSite, setAtSite] = useState(false)

  const onJob = new Set(job.contacts.map((link) => link.contact.id))
  const offers = [
    ...clientContacts(job.client.contacts).map((contact) => ({ contact, atSite: false })),
    ...(job.site ? siteContacts(job.client.contacts, job.site.id) : []).map((contact) => ({ contact, atSite: true })),
  ].filter(({ contact }) => !onJob.has(contact.id))
  const query = term.trim().toLowerCase()
  const matches = query ? offers.filter(({ contact }) => searchText(contact).includes(query)) : offers

  return (
    <FieldLayer title={draft ? 'New person' : 'Add a person'} subtitle={job.client.name} onClose={onClose}>
      <div className="mx-auto flex w-full max-w-[680px] flex-col gap-[18px] px-4 pt-4 pb-7">
        {draft ? (
          <FormCard>
            <ContactFields
              idPrefix={`field-new-${job.id}`}
              person={draft.firstName ? draft : null}
              submitLabel="Add to job"
              isPending={writes.create.isPending}
              error={writes.create.error ? errorMessage(writes.create.error) : null}
              atSite={atSite}
              onCancel={() => setDraft(null)}
              onSubmit={(contact) =>
                writes.create.mutate({ jobId: job.id, contact, atSite, roles: atSite ? ['SITE'] : ['JOB'] })
              }
            >
              {job.site ? (
                <label className="flex min-h-11 items-center gap-2.5 text-[14px] text-ink-700">
                  <input
                    type="checkbox"
                    checked={atSite}
                    onChange={(event) => setAtSite(event.target.checked)}
                    className="size-5 rounded border-ink-300 text-brand-600"
                  />
                  Only at this property (a tenant or site manager)
                </label>
              ) : null}
            </ContactFields>
          </FormCard>
        ) : (
          <>
            <label className="flex h-12 items-center gap-2 rounded-md bg-white px-3.5 text-ink-500 ring-1 ring-ink-200 ring-inset">
              <Icon name="search" className="size-[18px]" />
              <input
                type="search"
                aria-label="Find or add a person"
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                placeholder="Search the client’s people, or type a name"
                className="min-w-0 flex-1 bg-transparent text-base text-ink-900 outline-none placeholder:text-ink-400"
              />
            </label>

            <button
              type="button"
              onClick={() => setDraft(draftOf(term))}
              className={cn(
                cardClass,
                'flex min-h-[54px] w-full items-center gap-3 px-3.5 text-left text-[15px] font-semibold text-brand-700 active:bg-ink-50',
              )}
            >
              <span className="grid size-9 shrink-0 place-items-center rounded-md bg-brand-50">
                <Icon name="plus" className="size-4" strokeWidth={2.2} />
              </span>
              {term.trim() ? `New person “${term.trim()}”` : 'New person'}
            </button>

            {offers.length > 0 ? (
              <section>
                <GroupLabel>On the client</GroupLabel>
                <div className={cn(cardClass, 'divide-y divide-ink-100')}>
                  {matches.map(({ contact, atSite: site }) => (
                    <button
                      key={contact.id}
                      type="button"
                      disabled={writes.add.isPending}
                      onClick={() => writes.add.mutate({ jobId: job.id, contactId: contact.id, roles: site ? ['SITE'] : ['JOB'] })}
                      className="flex min-h-[56px] w-full items-center gap-3 px-3.5 py-2 text-left active:bg-ink-50 disabled:opacity-50"
                    >
                      <Initials name={nameOf(contact)} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[15px] font-semibold text-ink-900">{nameOf(contact)}</span>
                        <span className="block truncate text-[13px] text-ink-500">
                          {[site ? 'At this property' : null, titleOf(contact), contact.mobile ?? contact.phone ?? contact.email]
                            .filter(Boolean)
                            .join(' · ') || 'Client contact'}
                        </span>
                      </span>
                      <span className="shrink-0 text-[14px] font-semibold text-brand-700">Add</span>
                    </button>
                  ))}
                  {matches.length === 0 ? (
                    <p className="px-3.5 py-3 text-[14px] text-ink-500">Nobody on the client by that name.</p>
                  ) : null}
                </div>
              </section>
            ) : null}
            {writes.add.error ? (
              <p role="alert" className="px-1 text-[13px] text-red-700">
                {errorMessage(writes.add.error)}
              </p>
            ) : null}
          </>
        )}
      </div>
    </FieldLayer>
  )
}

/** The client screen's own fields, sized for a thumb: 16px text, so iOS does not zoom. */
function FormCard({ children }: { children: ReactNode }) {
  return (
    <div
      className={cn(
        cardClass,
        'p-3.5 [&_button]:min-h-11 [&_button]:flex-1 [&_button]:text-[15px] [&_input:not([type=checkbox])]:min-h-11 [&_input:not([type=checkbox])]:text-base [&_label]:text-[13px]',
      )}
    >
      {children}
    </div>
  )
}

function Reach({
  label,
  icon,
  href,
  onClick,
}: {
  label: string
  icon: 'phone' | 'inbox' | 'mail'
  href?: string | null
  onClick?: (() => void) | undefined
}) {
  const className =
    'flex h-[62px] flex-col items-center justify-center gap-1 rounded-md bg-white text-[13px] font-semibold text-brand-700 ring-1 ring-ink-200 ring-inset active:bg-ink-50'
  const body = (
    <>
      <Icon name={icon} className="size-5" />
      {label}
    </>
  )
  if (href) {
    return (
      <a href={href} className={className}>
        {body}
      </a>
    )
  }
  return (
    <button type="button" disabled={!onClick} onClick={onClick} className={cn(className, 'disabled:text-ink-300')}>
      {body}
    </button>
  )
}

function Segment({
  pressed,
  disabled,
  onClick,
  children,
}: {
  pressed: boolean
  disabled: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'h-9 rounded-sm text-[14px] font-semibold transition-colors',
        pressed ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500',
      )}
    >
      {children}
    </button>
  )
}

function Initials({ name }: { name: string }) {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
  return (
    <span
      aria-hidden="true"
      className="grid size-9 shrink-0 place-items-center rounded-md bg-ink-100 text-[13px] font-semibold text-ink-800 ring-1 ring-ink-200 ring-inset"
    >
      {letters || '?'}
    </span>
  )
}

type Named = Pick<ClientPerson, 'firstName' | 'lastName' | 'position' | 'phone' | 'mobile' | 'email'>

function nameOf(contact: Named): string {
  return [contact.firstName, contact.lastName].filter(Boolean).join(' ')
}

/** A job title. ServiceM8's type word ("JOB") is a role now, so it is not repeated. */
function titleOf(contact: Named): string | null {
  return positionIsRoleWord(contact.position) ? null : contact.position
}

function searchText(contact: Named): string {
  return [nameOf(contact), contact.position, contact.email, contact.mobile, contact.phone]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

function draftOf(typed: string): ClientPerson {
  const [firstName = '', ...rest] = typed.split(/\s+/).filter(Boolean)
  return {
    id: '',
    firstName,
    lastName: rest.join(' ') || null,
    position: null,
    email: null,
    phone: null,
    mobile: null,
    isPrimary: false,
    siteId: null,
  }
}
